import type { GuidelineRuleSnippet } from '../mock/guidelineSnippets';
import { getSnippetForCategory, GUIDELINE_SNIPPETS } from '../mock/guidelineSnippets';
import type { GuidelineDiffHunk, GuidelineChatProposal } from '../types/guidelineProposal';
import type { AccuracyGuidelineChatContext } from './accuracyGuidelineChat';
import { DRILLDOWN_LABELS } from './accuracyCountDrilldown';
import type { MetricCountKind } from './accuracyCountDrilldown';

const FIELD_LABELS: Record<string, string> = {
  existence_check_prompt: 'existence_check_prompt（存在フラグ）',
  violation_check_prompt: 'violation_check_prompt（違反検出）',
};

function pathLabel(snippet: GuidelineRuleSnippet): string {
  return `json_data/guidelines.json › ${snippet.guidelineName} › v${snippet.version} › ${snippet.categoryId} › ${snippet.ruleId}`;
}

function hunk(
  snippet: GuidelineRuleSnippet,
  startLine: number,
  parts: {
    contextBefore: string[];
    removed: string[];
    added: string[];
    contextAfter: string[];
    rationale: string;
  }
): GuidelineDiffHunk {
  return {
    pathLabel: pathLabel(snippet),
    field: snippet.field,
    fieldLabel: FIELD_LABELS[snippet.field] ?? snippet.field,
    startLine,
    ...parts,
  };
}

function resolveCategoryIds(ctx: AccuracyGuidelineChatContext): string[] {
  const fromDrilldown = ctx.drilldown.categoryId;
  if (fromDrilldown) return [fromDrilldown];
  const fromErrors = Array.from(
    new Set(
      ctx.sampleErrors
        .map((e) => e.category_id)
        .filter((id): id is string => Boolean(id))
    )
  );
  if (fromErrors.length > 0) return fromErrors.slice(0, 2);
  return Object.keys(GUIDELINE_SNIPPETS);
}

function buildFpHunks(ctx: AccuracyGuidelineChatContext): GuidelineDiffHunk[] {
  const hunks: GuidelineDiffHunk[] = [];
  const isExistence = ctx.drilldown.metricType === 'existence';

  for (const categoryId of resolveCategoryIds(ctx)) {
    if (isExistence) {
      const snippet = getSnippetForCategory(categoryId, 'existence_check_prompt');
      if (!snippet) continue;

      if (categoryId === 'overseas_travel_insurance') {
        hunks.push(
          hunk(snippet, 1, {
            contextBefore: [],
            removed: [
              '「海外旅行傷害保険」の記載を探し、記載が存在するか否かを出力してください。ある場合は「True」、ない場合は「False」を出力してください。',
            ],
            added: [
              'カード商品説明内に「海外旅行傷害保険」という語句が明示されている場合のみ True。単独の「海外旅行保険」「旅行保険」や、他社商品・一般論の「海外旅行」記載は False。',
              '脚注・注釈のみの言及、画像内で判読不能な場合は False。',
            ],
            contextAfter: [],
            rationale:
              '誤検知（存在フラグ）: 類似語のみで True になっているケースを抑えるため、必須語句と除外条件をプロンプトに追記します。',
          })
        );
      } else if (categoryId === 'interest_rate') {
        hunks.push(
          hunk(snippet, 1, {
            contextBefore: [],
            removed: ['アコムの利率・金利に関する記載の有無を出力してください。'],
            added: [
              'アコムの貸付利率（実質年率）または「3.0%～18.0%」の数値レンジが本文に明示されている場合のみ True。',
              '一般的な「金利」「利率」単語のみ、他社・比較表の金利記載は False。',
            ],
            contextAfter: [],
            rationale:
              '誤検知（存在フラグ）: 金利という語だけでヒットしている可能性があるため、対象商品と数値レンジを必須化します。',
          })
        );
      }
      continue;
    }

    const snippet = getSnippetForCategory(categoryId, 'violation_check_prompt');
    if (!snippet) continue;

    if (categoryId === 'overseas_travel_insurance') {
      hunks.push(
        hunk(snippet, 1, {
          contextBefore: ['### 重要な注意事項：'],
          removed: [
            '1. 保険名称の正確性：「海外旅行保険」は違反です。必ず「海外旅行傷害保険」と表記してください。国内も同じく「国内旅行傷害保険」と表記する必要がある',
          ],
          added: [
            '1. 保険名称の正確性：「海外旅行保険」は違反です。必ず「海外旅行傷害保険」と表記してください。国内も同じく「国内旅行傷害保険」と表記する必要がある',
            '   ※ ただし、他社カード・一般解説・比較表で「海外旅行保険」とあるだけの場合は違反としない（三菱UFJカード商品の付帯説明に限る）',
          ],
          contextAfter: [
            '2. 付帯条件の正確性：「利用付帯」「自動付帯」「自動付帯＋利用付帯」を正確に記載してください',
          ],
          rationale:
            '誤検知（違反）: 対象外コンテキストまで違反化しているため、判定対象を当該カード商品の付帯説明に限定します。',
        })
      );
    } else if (categoryId === 'interest_rate') {
      hunks.push(
        hunk(snippet, 1, {
          contextBefore: ['【注意】アコムに関する記載のみを対象としてください。'],
          removed: [
            '貸付利率（実質年率）は「3.0% ～ 18.0%」です。上限が18.0%を超えている場合や、範囲が異なる場合はNGです。',
          ],
          added: [
            '貸付利率（実質年率）は「3.0% ～ 18.0%」です。上限が18.0%を超えている場合や、範囲が異なる場合はNGです。',
            '※ 比較サイトの他社金利・過去キャンペーンの参考値は対象外とし、本文でアコムの現行レンジとして断定している場合のみ違反とする。',
          ],
          contextAfter: [],
          rationale:
            '誤検知（違反）: 参考・他社表記をアコムの断定として検出している可能性があるため、スコープを明記します。',
        })
      );
    }
  }

  return hunks;
}

function buildFnHunks(ctx: AccuracyGuidelineChatContext): GuidelineDiffHunk[] {
  const hunks: GuidelineDiffHunk[] = [];
  const isExistence = ctx.drilldown.metricType === 'existence';

  for (const categoryId of resolveCategoryIds(ctx)) {
    if (isExistence) {
      const snippet = getSnippetForCategory(categoryId, 'existence_check_prompt');
      if (!snippet) continue;

      if (categoryId === 'overseas_travel_insurance') {
        hunks.push(
          hunk(snippet, 1, {
            contextBefore: [
              '三菱ＵＦＪカード、三菱ＵＦＪカード ゴールド、三菱ＵＦＪカード・プラチナ・アメリカン・エキスプレス®・カード、ＶＩＡＳＯカード、ＶＩＡＳＯカード（キャラクター券面）の',
            ],
            removed: [
              '「海外旅行傷害保険」の記載を探し、記載が存在するか否かを出力してください。ある場合は「True」、ない場合は「False」を出力してください。',
            ],
            added: [
              '「海外旅行傷害保険」「海外旅行保険」「旅行傷害保険」および付帯・補償・利用付帯に関する記載を探し、存在するか出力してください（表記ゆれは True 候補）。',
              '画像・表・脚注内の記載も対象。判読できる場合は True。',
            ],
            contextAfter: [],
            rationale:
              '検知漏れ（存在フラグ）: 表記ゆれや画像内テキストで見逃しているため、同義語とレイアウトをプロンプトに含めます。',
          })
        );
      } else if (categoryId === 'interest_rate') {
        hunks.push(
          hunk(snippet, 1, {
            contextBefore: [],
            removed: ['アコムの利率・金利に関する記載の有無を出力してください。'],
            added: [
              'アコムの利率・金利・実質年率・％表記（例: 3.0%、18.0%）・「借入利率」に関する記載の有無を出力してください（表・注釈・キャプション含む）。',
            ],
            contextAfter: [],
            rationale:
              '検知漏れ（存在フラグ）: 数値や別表記の金利説明を拾えていないため、マッチ対象語を拡張します。',
          })
        );
      }
      continue;
    }

    const snippet = getSnippetForCategory(categoryId, 'violation_check_prompt');
    if (!snippet) continue;

    if (categoryId === 'overseas_travel_insurance') {
      hunks.push(
        hunk(snippet, 4, {
          contextBefore: [
            '2. 付帯条件の正確性：「利用付帯」「自動付帯」「自動付帯＋利用付帯」を正確に記載してください',
            '4. 補償金額の正確性：各カード毎に定められた正確な金額を記載してください',
          ],
          removed: [],
          added: [
            '6. 検出漏れ防止：「最大○○万円」「○○円まで」等、金額が表・画像・脚注にある場合も必ずカード別の正規額と照合し、不一致なら違反とする',
          ],
          contextAfter: [
            '5. カード毎の保険内容が異なることに注意してください',
          ],
          rationale:
            '検知漏れ（違反）: 手動追加された違反が金額表記の見落としである場合、表・脚注の金額照合を明示します。',
        })
      );
    } else if (categoryId === 'interest_rate') {
      hunks.push(
        hunk(snippet, 2, {
          contextBefore: ['【注意】アコムに関する記載のみを対象としてください。'],
          removed: [
            '貸付利率（実質年率）は「3.0% ～ 18.0%」です。上限が18.0%を超えている場合や、範囲が異なる場合はNGです。',
          ],
          added: [
            '貸付利率（実質年率）は「3.0% ～ 18.0%」です。上限が18.0%を超えている場合や、範囲が異なる場合はNGです。',
            '「最大18%」「年18.0%」「実質年率○%」など表記ゆれ・単独上限表記もレンジと照合し、18.0%超または下限3.0%未満の断定は違反とする。',
          ],
          contextAfter: [],
          rationale:
            '検知漏れ（違反）: レンジ外の単独％表記を見逃しているため、表記バリエーションを violation_check_prompt に追記します。',
        })
      );
    }
  }

  return hunks;
}

function summaryForKind(ctx: AccuracyGuidelineChatContext, kind: MetricCountKind): string {
  const d = ctx.drilldown;
  const metric = DRILLDOWN_LABELS.metricType[d.metricType];
  const count = DRILLDOWN_LABELS.countKind[kind];
  return `${d.categoryName} · ${metric} · ${count}（${ctx.totalItemCount} 件）向けのガイドライン修正案です。赤行を削除・緑行を追加する想定です（モック）。`;
}

export function buildGuidelineChatProposal(
  ctx: AccuracyGuidelineChatContext,
  userMessage: string
): GuidelineChatProposal {
  const wantsFn = /検知漏れ|漏れ|fn|見逃し/.test(userMessage);
  const wantsFp = /誤検知|fp|誤検出|過検知|厳し/.test(userMessage);

  let kind: MetricCountKind = ctx.drilldown.countKind;
  if (wantsFn && !wantsFp) kind = 'fn';
  else if (wantsFp && !wantsFn) kind = 'fp';

  if (kind === 'tp' || kind === 'wb') {
    return {
      summary:
        kind === 'tp'
          ? '正検知の件数です。改善は誤検知・検知漏れの列から提案を依頼してください。'
          : '枠ずれはガイドライン文言より検出位置の調整が中心です。誤検知・検知漏れとは別途対応してください。',
      hunks: [],
    };
  }

  const hunks = kind === 'fn' ? buildFnHunks(ctx) : buildFpHunks(ctx);

  if (hunks.length === 0) {
    return {
      summary: `対象カテゴリ（${ctx.drilldown.categoryName}）のサンプルプロンプトが未登録です。ガイドライン管理で該当カテゴリの existence_check_prompt / violation_check_prompt を確認してください。`,
      hunks: [],
    };
  }

  return {
    summary: summaryForKind(ctx, kind),
    hunks,
  };
}

/** Static sample for empty chat state (GitHub-style preview). */
export function buildWelcomeSampleProposal(
  ctx: AccuracyGuidelineChatContext
): GuidelineChatProposal | null {
  if (ctx.drilldown.countKind === 'tp' || ctx.drilldown.countKind === 'wb') {
    return null;
  }
  const kind = ctx.drilldown.countKind === 'fn' ? 'fn' : 'fp';
  const prompt =
    kind === 'fn'
      ? '検知漏れを減らすためのガイドライン修正案を提案してください'
      : '誤検知を減らすためのガイドライン修正案を提案してください';
  const proposal = buildGuidelineChatProposal(ctx, prompt);
  if (proposal.hunks.length === 0) return null;
  return proposal;
}
